import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';

import { SEVERITIES } from '../Constants/Rule';
import TemplateService from '../Services/TemplateService';
const templateService = new TemplateService();

export default class RuleUtils {
  ruleTypeDisplayMap = new Map([
    ['lessThan', 'Less Than'],
    ['greaterThan', 'Greater Than'],
    ['between', 'Between'],
    ['outsideOf', 'Outside'],
    ['equalTo', 'Equals'],
    ['notEqualTo', 'Does Not Equal'],
    ['contains', 'Contains'],
    ['startsWith', 'Starts With'],
    ['endsWith', 'Ends With'],
  ]);

  ruleSeverityDisplayMap = new Map(
    SEVERITIES.map((s) => [s, s.charAt(0).toUpperCase() + s.slice(1)])
  );

  frequencyIntervalMap = new Map([
    ['second(s)', 1],
    ['minutes(s)', 60],
    ['hours(s)', 60 * 60],
    ['days(s)', 60 * 60 * 24],
  ]);

  secondsInDay = 60 * 60 * 24;
  secondsInHour = 60 * 60;
  secondsInMinute = 60;

  parseFrequencyFromSeconds(value) {
    switch (true) {
      case value >= this.secondsInDay:
        return {
          frequencyValue: String(value / this.secondsInDay),
          selectedFrequency: {
            label: 'days(s)',
            value: this.frequencyIntervalMap.get('days(s)'),
          },
        };
      case value >= this.secondsInHour:
        return {
          frequencyValue: String(value / this.secondsInHour),
          selectedFrequency: {
            label: 'hours(s)',
            value: this.frequencyIntervalMap.get('hours(s)'),
          },
        };
      case value >= this.secondsInMinute:
        return {
          frequencyValue: String(value / this.secondsInMinute),
          selectedFrequency: {
            label: 'minutes(s)',
            value: this.frequencyIntervalMap.get('minutes(s)'),
          },
        };
      default:
        return {
          frequencyValue: String(value),
          selectedFrequency: {
            label: 'second(s)',
            value: this.frequencyIntervalMap.get('second(s)'),
          },
        };
    }
  }

  parseSeverityForDisplay(severity) {
    if (isEmpty(severity)) return null;
    const label = this.ruleSeverityDisplayMap.get(severity);
    return { value: severity, label };
  }

  findModelBySourceId(gtemplateID, templates) {
    const target = templates.find(({ _id }) => _id === gtemplateID);
    const selectedModel = target ? { label: target.name, value: target } : null;
    return { selectedModel };
  }

  _handleConditionSignalCollectionAndSignal(
    collectionsMap = new Map(),
    collectionID,
    signalID
  ) {
    const targetCollection = collectionsMap.get(collectionID);
    if (isEmpty(targetCollection)) {
      return {
        selectedSignalCollection: null,
        selectedSignal: null,
        signals: [],
      };
    }

    const selectedSignalCollection = {
      label: targetCollection.name,
      value: targetCollection,
    };
    const { values } = targetCollection;
    if (isEmpty(values)) {
      return { selectedSignalCollection, selectedSignal: null, signals: [] };
    }

    const signals = values.map((value) => {
      return { label: value.name || '', value };
    });

    const targetSignal = values.find(({ _id }) => _id === signalID);
    if (isEmpty(targetSignal)) {
      return { selectedSignalCollection, selectedSignal: null, signals };
    }

    const selectedSignal = { label: targetSignal.name, value: targetSignal };

    let unit = targetSignal.unit;
    if (
      Array.isArray(targetSignal.transforms) &&
      targetSignal.transforms.length
    ) {
      const [{ uom }] = targetSignal.transforms;
      unit = uom ?? targetSignal.unit;
    }

    return { selectedSignalCollection, selectedSignal, signals, unit };
  }

  _handleOutputSignalCondtions(conditions, templateID, cancelToken) {
    const outputSignalRequests = conditions.map(async (c, i) => {
      const { SignalId, Type, Value } = c;

      const selectedConditionType = this._handleSelectedConditionType(Type);
      const selectedSignalCollection = {
        label: 'Calculated Signal',
        value: 'outputSignal',
      };

      const outputSignal = await templateService.fetchOutputSignal(
        templateID,
        SignalId,
        cancelToken
      );

      const selectedSignal = { label: outputSignal?.name, value: outputSignal };

      return {
        key: `${SignalId}-${i}`,
        selectedConditionType,
        selectedSignal,
        selectedSignalCollection,
        signals: [],
        ...this._handleConditionValue(Value),
      };
    });
    return Promise.all(outputSignalRequests);
  }

  _handleSelectedConditionType(type) {
    const label = this.ruleTypeDisplayMap.get(type);
    if (!label) return null;

    return { value: type, label };
  }

  _handleConditionValue(value) {
    if (Array.isArray(value)) {
      const [low, high] = value;
      return { singleOrLowValue: String(low), highValue: String(high) };
    }
    return { singleOrLowValue: String(value) };
  }

  async transformConditionsForDisplay(Conditions, model, cancelToken) {
    if (isEmpty(Conditions) || isEmpty(model)) {
      const key = String(Math.ceil(Math.random() * 100000000000));
      return [{ key }];
    }

    const collectionsMap = new Map(
      model.datapoints?.map?.((sc) => [sc?._id, sc]) || []
    );

    const { eventCollectionConditions, outputSignalConditions } =
      Conditions.reduce(
        (acc, condition) => {
          if (collectionsMap.has(condition.SignalCollectionId)) {
            acc.eventCollectionConditions.push(condition);
          } else {
            acc.outputSignalConditions.push(condition);
          }
          return acc;
        },
        {
          eventCollectionConditions: [],
          outputSignalConditions: [],
        }
      );
    const eventConditions = eventCollectionConditions.map((c, i) => {
      const { SignalId, SignalCollectionId, Type, Value } = c;

      const { selectedSignalCollection, selectedSignal, signals, unit } =
        this._handleConditionSignalCollectionAndSignal(
          collectionsMap,
          SignalCollectionId,
          SignalId
        );
      const selectedConditionType = this._handleSelectedConditionType(Type);

      const displayProps = {
        key: `${SignalId}-${i}`,
        selectedSignalCollection,
        selectedSignal,
        signals,
        unit,
        selectedConditionType,
        ...this._handleConditionValue(Value),
      };

      return { ...c, ...displayProps };
    });
    const outputSignalConditionProms = await this._handleOutputSignalCondtions(
      outputSignalConditions,
      model?._id,
      cancelToken
    );
    return eventConditions.concat(outputSignalConditionProms);
  }

  parseCondition(datapointMap, datapointID, signalID) {
    const targetCollection = datapointMap[datapointID];
    if (isEmpty(targetCollection.values)) return {};

    const targetSignal = targetCollection.values.find(
      ({ _id }) => _id === signalID
    );
    if (isEmpty(targetSignal)) return {};

    const { name, label, unit = '' } = targetSignal;

    return {
      signalCollectionName: targetCollection.name,
      signalName: name || label,
      unit,
    };
  }

  parseThresholdValue(value) {
    return Array.isArray(value) ? value.join(' | ') : value;
  }

  transformRulesForDisplay(asset, rules = []) {
    const {
      gtemplate: { datapoints },
    } = asset;
    const datapointMap = keyBy(datapoints, '_id');
    return rules
      .map(({ Id, Name, Conditions, Enabled, Frequency }) => {
        if (isEmpty(Conditions)) return;
        const ProcessedConditions = Conditions.map(
          ({ SignalCollectionId, SignalId, Type, Value }) => {
            const { signalCollectionName, signalName, unit } =
              this.parseCondition(datapointMap, SignalCollectionId, SignalId);
            const threshold = this.ruleTypeDisplayMap.get(Type);
            const value = this.parseThresholdValue(Value);

            return {
              threshold,
              value,
              signalCollectionName,
              signalName,
              SignalId,
              unit,
            };
          }
        );

        return {
          Id,
          Name,
          Enabled,
          Frequency,
          Conditions: ProcessedConditions,
        };
      })
      .filter((r) => !!r);
  }

  addSubscriptionsToRules(rules, subs) {
    const ruleSubsDictionary = subs.reduce((dict, sub) => {
      if (sub.Trigger === 'dm1') return dict;
      dict[sub.RuleId] = sub;
      return dict;
    }, {});

    return rules.map((rule) => {
      const { Notification, Id } = ruleSubsDictionary[rule.Id] || {};
      if (!Id || !Notification) return rule;

      const Subscription = { [Notification]: ruleSubsDictionary[rule.Id] };
      return Object.assign({}, rule, Subscription);
    });
  }
}
